/* eslint-disable react/jsx-key */
import React from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import { generateReasonsLabel } from 'src/utils/getReferralData';
import { flattenMetadataEntries } from 'src/utils/formatMetadataValue';
// @mui
import { Stack, Box, Typography } from '@mui/material';
import { Iconify } from '../../../components';
import {
    sourceCardsRow,
    sourceCard,
    sourceCardHeader,
    sourceCardIconBox,
    sourceCardLabel,
    sourceCardValue,
    metadataContainer,
    metadataHeader,
    metadataTitle,
    metadataCount,
    metadataTableHead,
    metadataTableHeadCell,
    metadataTableRow,
    metadataTableKey,
    metadataTableValue,
    emptyStateBox,
    emptyStateText,
} from '../../../styles/MetaSourceStyle';
// redux
import { useSelector } from '../../../redux/store';
import DemoBlur from '../../../components/DemoBlur';
import { getTikTokMetadataSections } from './tiktokMetadata';

// ------------------------------------------------------------------

export default function MetaSource({ leadDetail }) {
    const metaData = leadDetail && leadDetail?.lead_metadata;
    const leadSourceMetaData = leadDetail && leadDetail?.lead_source && leadDetail?.lead_source?.metadata;
    const { referral, sourceType } = useSelector((state) => state.schema);
    const theme = useTheme();
    const isLight = theme.palette.mode === 'light';

    const matchedSource = sourceType?.find((e) => leadDetail?.lead_source?.type === e.value);
    const matchedReferral = referral?.find((e) => leadDetail?.lead_source?.referral === e.value);

    const referralDetail = typeof leadSourceMetaData?.referral_detail === 'string' ? leadSourceMetaData.referral_detail : '';
    const isTikTok = leadDetail?.lead_source?.referral === 'TIKTOK';
    const metadataSections = isTikTok ? getTikTokMetadataSections(metaData, leadSourceMetaData) : [{
        title: 'Metadata',
        entries: [
            ...(metaData || []).map((el) => ({ key: el?.key ? generateReasonsLabel(el.key) : '', value: el?.value ? generateReasonsLabel(el.value) : '' })),
            ...flattenMetadataEntries(leadSourceMetaData),
        ],
    }];

    return (
        <Stack spacing={3}>
            {/* Source & Referral Cards */}
            <Box sx={sourceCardsRow}>
                <Box sx={sourceCard('#FF6C40')}>
                    <Box sx={sourceCardHeader}>
                        <Box sx={sourceCardIconBox('rgba(255,108,64,0.12)')}>
                            {matchedSource && <Iconify icon={matchedSource.icon} color={'#FF6C40'} width={24} height={24} />}
                            {!matchedSource && <Iconify icon={'mdi:web'} color={'#FF6C40'} width={24} height={24} />}
                        </Box>
                        <Box>
                            <Typography sx={sourceCardLabel}>Lead Source Type</Typography>
                            <Typography sx={sourceCardValue}>{matchedSource?.label || '—'}</Typography>
                        </Box>
                    </Box>
                </Box>
                <Box sx={sourceCard('#E02D69')}>
                    <Box sx={sourceCardHeader}>
                        <Box sx={sourceCardIconBox('rgba(224,45,105,0.12)')}>
                            {matchedReferral && <Iconify icon={matchedReferral.icon} color={'#E02D69'} width={24} height={24} />}
                            {!matchedReferral && <Iconify icon={'mdi:account-arrow-left'} color={'#E02D69'} width={24} height={24} />}
                        </Box>
                        <Box>
                            <Typography sx={sourceCardLabel}>Lead Source Referral</Typography>
                            <Typography sx={sourceCardValue}>
                                {matchedReferral ? matchedReferral.label : '—'}
                                {matchedReferral && referralDetail ? <>{' · '}<DemoBlur>{referralDetail}</DemoBlur></> : null}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Metadata Table */}
            {metadataSections.map(({ title, entries: metaEntries }) => (
                <Box key={title} role={isTikTok ? 'region' : undefined} aria-label={isTikTok ? title : undefined} sx={metadataContainer}>
                    <Box sx={metadataHeader}>
                        <Iconify icon={'mdi:database-outline'} width={20} height={20} color={'#64748b'} />
                        <Typography sx={metadataTitle}>{title}</Typography>
                        {metaEntries.length > 0 && (
                            <Typography sx={metadataCount}>{metaEntries.length}</Typography>
                        )}
                    </Box>
                    {metaEntries.length > 0 ? (
                        <>
                            <Box sx={metadataTableHead}>
                                <Typography sx={metadataTableHeadCell}>Field</Typography>
                                <Typography sx={metadataTableHeadCell}>Value</Typography>
                            </Box>
                            {metaEntries.map((entry, index) => (
                                <Box key={index} sx={metadataTableRow(isLight)}>
                                    {isTikTok ? (
                                        <Box sx={metadataTableKey}>
                                            <Typography component='span' sx={{ font: 'inherit' }}>{entry.key}</Typography>
                                            {entry.originLabel && (
                                                <Typography component='span' variant='caption' sx={{ display: 'block', color: 'text.secondary' }}>{entry.originLabel}</Typography>
                                            )}
                                        </Box>
                                    ) : <Typography sx={metadataTableKey}>{entry.key}</Typography>}
                                    <Typography sx={isTikTok ? { ...metadataTableValue, whiteSpace: 'pre-wrap' } : metadataTableValue}><DemoBlur>{entry.value}</DemoBlur></Typography>
                                </Box>
                            ))}
                        </>
                    ) : (
                        <Box sx={emptyStateBox}>
                            <Iconify icon={'mdi:database-off-outline'} width={48} height={48} color={'#cbd5e1'} />
                            <Typography sx={emptyStateText}>No metadata available</Typography>
                        </Box>
                    )}
                </Box>
            ))}
        </Stack>
    );
}

MetaSource.propTypes = {
    leadDetail: PropTypes.any,
};
